import _ from 'lodash';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import React from 'react';
import gsap from 'gsap';
import classnames from 'classnames';
import Link from 'next/link';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

import { useUserAuth } from 'contexts/UserAuth';
import redirectToLogin from 'utils/redirectToLogin';
import { useWindowSize } from 'react-use';
import WebpImg from 'components/WebpImg';

import api, { swrFetcher } from 'utils/api';

import AnimateTargetNumber from 'components/AnimateTargetNumber';
import { SorraCircle, SorraStar, RotateItem } from 'components/2025/SorraDecos';
// import Beauty2024Ring from 'components/Beauty2024Ring';
// import Beauty2024RingX15 from 'components/Beauty2024RingX15';
// import { giftList } from 'utils/voting2025';

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);

const { useState, useEffect, useRef, forwardRef } = React;

const ScrollingGifts = (props) => {
  const { awards } = props;
  return (
    <div
      style={{
        boxShadow: '0 -4px 20px 0 rgba(0, 0, 0, 0.05)',
      }}
      className="relative w-full overflow-hidden bg-white py-2 lg:py-4"
    >
      <div className="relative box-border block h-full min-w-max animate-[halfLeft_16s_linear_infinite] whitespace-nowrap">
        {Array(2)
          .fill(0)
          .map((n, loopIndex) => {
            return (
              <div
                key={loopIndex}
                className="inline-block h-full w-fit min-w-max px-2"
              >
                {awards.map((award) => {
                  const {
                    name,
                    display_name: displayName,
                    scrolling_name: scrollingName,
                  } = award;
                  return (
                    <div key={award.id} className="inline-block px-2">
                      <div className="flex flex-row items-center justify-center gap-x-1.5">
                        <WebpImg
                          src={
                            award.image
                              ? award.image.url
                              : '/images/placeholders/product.png'
                          }
                          className="aspect-square w-15 shrink-0 object-contain"
                        />
                        <div className="shrink-0 text-black">
                          <p className="whitespace-pre-line text-sm font-medium">
                            {scrollingName || displayName || name}
                          </p>
                          <p className="text-xs">{award.price}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
      </div>
    </div>
  );
};

const SpinningNumber = (props) => {
  const {
    number,
    width = 20,
    fontSize = 28,
    duration = 0.7,
    animate = true,
  } = props;
  const digits = number
    .toString()
    .split('')
    .map((digit) => parseInt(digit, 10));
  const height = fontSize * 1.25 + 8;
  return (
    <>
      {digits.map((digit, index) => {
        return (
          <React.Fragment key={index}>
            <AnimateTargetNumber
              target={digit}
              repeat={index + 1}
              animate={animate}
              duration={duration}
              width={width}
              fontSize={fontSize}
              className="!font-jinxuanlatte !font-bold"
            />
            {(digits.length - (index + 1)) % 3 == 0 &&
              index != digits.length - 1 && (
                <div
                  style={{
                    height: `${height}px`,
                    fontSize: `${fontSize}px`,
                  }}
                  className="relative flex items-center"
                >
                  <p className="text-end font-jinxuanlatte font-bold text-sorra-pink">
                    ,
                  </p>
                </div>
              )}
          </React.Fragment>
        );
      })}
    </>
  );
};

const BeautyAwardCTA = (props) => {
  const router = useRouter();
  const { disableVoting, expired } = props;
  return (
    <button
      disabled={disableVoting}
      onClick={() => {
        if (expired) {
          router.push({
            pathname: `/beautyawards2025/result`,
          });
        } else {
          router.push({
            pathname: `/beautyawards2025/questions/1`,
            query: router.query,
          });
        }
      }}
      className="w-full rounded-full bg-sorra-pink py-3 text-center font-sans text-base font-medium text-white hover:opacity-70 disabled:pointer-events-none disabled:bg-[#F7AFB8]"
    >
      {expired
        ? '查看票選結果'
        : disableVoting
          ? '你已投選，謝謝參與!'
          : '立即投票'}
    </button>
  );
};

const BeautyAwards2025 = (props) => {
  const { awards = [], expired = false } = props;

  const router = useRouter();

  const { pathname, asPath, isReady } = router;

  const { user, getAuthorizationHeader, isSignInStatusReady } = useUserAuth();

  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const [desktopScale, setDesktopScale] = useState(1);
  const desktopBaseWidth = 1440;
  const desktopBaseHeight = 705;

  const isLoginPage = pathname === '/beautyawards2025/login';

  const checkCurrentStatus = async () => {
    if (!expired) {
      const headers = await getAuthorizationHeader();
      try {
        await api.request({
          method: 'GET',
          url: `/api/beauty-award/me/votingAnswer`,
          headers,
        });
        setDisableVoting(true);
      } catch (e) {
        return;
      }
      try {
        await api.request({
          method: 'GET',
          url: `/api/beauty-award/me/luckyDraw`,
          headers,
        });
      } catch (e) {
        router.push({
          pathname: `/beautyawards2025/luckyreels`,
          query: router.query,
        });
      }
    }
  };

  useEffect(() => {
    if (isReady && isSignInStatusReady && isLoginPage) {
      if (user) {
        const existed = localStorage.getItem('beauty_award_2025_votings');
        if (existed) {
          router.push({
            pathname: '/beautyawards2025/luckyreels',
          });
        } else {
          router.push({
            pathname: '/beautyawards2025/questions/1',
          });
        }
      }
    } else if (isReady && isSignInStatusReady && !isLoginPage) {
      if (user) {
        checkCurrentStatus();
      }
    }
  }, [isReady, isSignInStatusReady, isLoginPage, user]);

  // const [animateTrigger, setAnimateTrigger] = useState(false);
  const [originStrategy, setOriginStrategy] = useState('center center');

  const planeRef = useRef(null);
  const redBowRef = useRef(null);
  const blueBowRef = useRef(null);
  const lipRef = useRef(null);
  const ballonRef = useRef(null);
  const desktopFrameRef = useRef(null);

  const mobileEarthRef = useRef(null);
  const mobilePlaneRef = useRef(null);
  const mobileBallonRef = useRef(null);
  const mobileRabbitRef = useRef(null);

  const [featureFirst, setFeatureFirst] = useState(0);

  const [animateTrigger, setAnimateTrigger] = useState(false);
  const [disableVoting, setDisableVoting] = useState(false);

  useEffect(() => {
    let interval = null;
    interval = setInterval(() => {
      setFeatureFirst((prev) => {
        return (prev + 1) % (3 * 2);
      });
    }, 3500);
    return () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
  }, []);

  useGSAP(() => {
    const selectedIdx = [
      featureFirst > 0 ? featureFirst - 1 : 5,
      featureFirst,
      (featureFirst + 1) % 6,
      (featureFirst + 2) % 6,
    ];
    const unselectedIdx = [];
    for (let i = 0; i < 6; i++) {
      if (!selectedIdx.includes(i)) {
        unselectedIdx.push(i);
      }
    }
    gsap.to(`.feature-${selectedIdx[0]}`, {
      top: '20%',
      scale: 0.77,
      y: 0,
      x: '-100%',
      left: '0%',
      duration: 0.8,
    });
    gsap.to(`.feature-${selectedIdx[1]}`, {
      top: '20%',
      scale: 0.77,
      y: 0,
      x: '-60%',
      left: '0%',
      duration: 0.8,
    });
    gsap.to(`.feature-${selectedIdx[2]}`, {
      top: 0,
      scale: 1,
      y: 0,
      x: '-50%',
      left: '50%',
      duration: 0.8,
    });
    gsap.to(`.feature-${selectedIdx[3]}`, {
      top: '20%',
      scale: 0.77,
      y: 0,
      x: '-40%',
      left: '100%',
      duration: 0.8,
    });

    gsap.set(
      unselectedIdx
        .map((i) => {
          return '.feature-' + i;
        })
        .join(','),
      {
        top: '20%',
        scale: 0.77,
        y: 0,
        x: '100%',
        left: '100%',
        duration: 0.8,
      }
    );
  }, [featureFirst]);

  useGSAP(() => {
    gsap.fromTo(
      planeRef.current,
      {
        opacity: 0,
        x: 50,
        y: -50,
        scale: 2,
      },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        duration: 0.8,
        ease: 'power2.in',
        onComplete: () => {
          // After fade in completes, start floating animation
          gsap.to(planeRef.current, {
            y: '+=15',
            duration: 2,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );
    gsap.fromTo(
      ballonRef.current,
      {
        opacity: 0,
        x: -100,
        y: -100,
        scale: 2,
      },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        duration: 1.2,
        ease: 'power2.out',
        onComplete: () => {
          // After fade in completes, start floating animation
          gsap.to(ballonRef.current, {
            y: '+=10',
            duration: 3,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );
    gsap.fromTo(
      lipRef.current,
      {
        opacity: 0,
        x: -100,
        y: +100,
        scale: 2,
      },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        duration: 1.2,
        ease: 'power2.out',
        onComplete: () => {
          // After fade in completes, start floating animation
          gsap.to(lipRef.current, {
            y: '+=10',
            duration: 3,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );
    gsap.fromTo(
      redBowRef.current,
      {
        opacity: 0,
        x: -100,
        scale: 2,
      },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        duration: 1.2,
        ease: 'power2.out',
        onComplete: () => {
          // After fade in completes, start floating animation
          gsap.to(redBowRef.current, {
            y: '+=5',
            duration: 2,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );
    gsap.fromTo(
      redBowRef.current,
      {
        opacity: 0,
        x: -100,
        scale: 2,
      },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        duration: 1.2,
        ease: 'power2.out',
        onComplete: () => {
          // After fade in completes, start floating animation
          gsap.to(redBowRef.current, {
            y: '+=5',
            duration: 2,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );
    gsap.fromTo(
      blueBowRef.current,
      {
        opacity: 0,
        x: 100,
        scale: 2,
      },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        duration: 1.2,
        ease: 'power2.out',
        onComplete: () => {
          gsap.to(blueBowRef.current, {
            y: '+=8',
            duration: 2,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );
    gsap.fromTo(
      desktopFrameRef.current,
      {
        opacity: 0,
      },
      {
        opacity: 1,
        duration: 2,
        ease: 'power2.out',
      }
    );

    gsap.fromTo(
      mobileEarthRef.current,
      {
        rotate: 0,
      },
      {
        rotate: -360,
        duration: 13,
        ease: 'linear',
        repeat: -1,
      }
    );

    gsap.fromTo(
      mobilePlaneRef.current,
      {
        opacity: 0,
        x: '-100%',
        y: '-100%',
        scale: 2,
        rotate: -3.019,
      },
      {
        opacity: 1,
        scale: 1,
        x: '0%',
        y: '-50%',
        duration: 1.2,
        rotate: -3.019,
        ease: 'power2.in',
        onComplete: () => {
          gsap.to(mobilePlaneRef.current, {
            y: '+=8',
            duration: 2,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );

    gsap.fromTo(
      mobileBallonRef.current,
      {
        opacity: 0,
        x: 40,
        y: 40,
        scale: 2,
      },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        duration: 1.2,
        ease: 'power2.out',
        onComplete: () => {
          gsap.to(mobileBallonRef.current, {
            y: '+=8',
            duration: 2,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      }
    );

    return () => {
      gsap.killTweensOf(planeRef.current);
      gsap.killTweensOf(ballonRef.current);
      gsap.killTweensOf(redBowRef.current);
      gsap.killTweensOf(blueBowRef.current);
      gsap.killTweensOf(desktopFrameRef.current);

      gsap.killTweensOf(mobileEarthRef.current);
      gsap.killTweensOf(mobilePlaneRef.current);
      gsap.killTweensOf(mobileBallonRef.current);
      // gsap.killTweensOf('.mobileCard');
    };
  }, []);

  useEffect(() => {
    if (windowWidth && windowHeight) {
      const baseProportion = 705 / 1440;
      const currentProportion = windowHeight / windowWidth;
      if (currentProportion >= baseProportion) {
        setDesktopScale(windowWidth / desktopBaseWidth);
        setOriginStrategy('center center');
      } else {
        setDesktopScale(windowHeight / desktopBaseHeight);
        setOriginStrategy('center center');
      }
    }
  }, [windowWidth, windowHeight]);

  // const { data: myVotingAnswer } = useSWR(
  //   user ? '/api/beauty-award/me/votingAnswer' : null,
  //   (url) => swrFetcher(url, getAuthorizationHeader),
  //   {
  //     revalidateOnFocus: false,
  //   }
  // );

  // useEffect(() => {
  //   if (isSignInStatusReady && myVotingAnswer) {
  //     setDisableVoting(true);
  //   }
  // }, [isSignInStatusReady, myVotingAnswer]);

  useGSAP(() => {
    ScrollTrigger.create({
      scroller: '#app-wrapper',
      start: 'top 80%',
      end: 'bottom 30%',
      trigger: '.animateTrigger',
      // markers: true,
      onEnter: () => {
        setAnimateTrigger(true);
      },
    });
    gsap.fromTo(
      '.award',
      {
        y: '+=120',
        opacity: 0,
      },
      {
        scrollTrigger: {
          scroller: '#app-wrapper',
          trigger: '.prize',
          start: 'top 70%',
          end: 'bottom 30%',
          toggleActions: 'play none none reverse',
        },
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: {
          amount: 1,
          grid: 'auto',
          axis: 'y',
          from: 'start',
        },
      }
    );

    gsap.fromTo(
      mobileRabbitRef.current,
      {
        y: '40',
        x: '40',
        opacity: 0,
      },
      {
        scrollTrigger: {
          // markers: true,
          scroller: '#app-wrapper',
          trigger: '.mobileRabbitTrigger',
          start: 'top 70%',
          end: 'bottom 30%',
          toggleActions: 'play none none reverse',
        },
        y: 0,
        x: '33%',
        opacity: 1,
        duration: 0.8,
      }
    );

    return () => {
      ScrollTrigger.killAll();
    };
  }, []);

  useEffect(() => {
    if (user && isLoginPage) {
      router.replace({
        pathname: '/beautyawards2025/luckyreels',
      });
    }
  }, [user, isLoginPage]);

  const loopItems = [
    Array(5).fill(['爽膚', '精華', '面霜', '防曬']),
    Array(5).fill(['胭脂', '眼影', '唇妝產品', '眉部產品']),
    Array(5).fill(['底妝遮瑕', '定妝​', '香水', '洗面卸妝']),
  ];

  const [hoverState, setHoverState] = useState([]);

  return (
    <>
      <NextSeo
        title="Sa Sa 美麗「程」獻：2025 Sorra Beauty Awards |  最真實的美妝產品評價 | Sorra"
        description="Sa Sa x Sorra年度票選大賞正式開始！第2屆的主題為「Fly me to the ...」，希望大家在選出陪你渡過2025年旅程的年度好物，打造香港最貼地的美妝排行榜。"
        openGraph={{
          images: [
            {
              url: 'https://www.sorra.net/images/beauty-award/2025/banner_launching.png',
              width: 1200,
              height: 410,
              alt: 'Sorra',
            },
          ],
        }}
      />
      <div
        style={{
          background:
            'linear-gradient(90deg, #FACBD1 1.2%, #FFF5F6 30.36%, #FACBD1 99.86%)',
        }}
        className="relative hidden h-screen w-screen flex-col items-center justify-center overflow-visible text-white lg:flex"
      >
        <div
          style={{
            backfaceVisibility: 'hidden',
            transform: `perspective(1px) translateZ(0) scale(${desktopScale})`,
            transformOrigin: originStrategy,
          }}
          className="relative flex h-[705px] w-[1440px] flex-col justify-center overflow-visible bg-transparent"
        >
          <div className="relative flex shrink-0 flex-grow flex-row gap-x-10 overflow-visible bg-transparent">
            <div className="absolute bottom-0 left-0 right-0 top-0">
              <SorraCircle className="left-[217px] top-[48px] h-3 w-3" />
              <SorraCircle className="left-[106px] top-[39px] h-4 w-4" />
              <SorraStar className="left-[39px] top-[150px] h-[29px] w-5 rotate-[-12deg]" />
              <SorraCircle className="left-[29px] top-[267px] h-2.5 w-2.5" />
              <SorraCircle className="bottom-[122px] left-[40px] h-2.5 w-2.5" />
              <SorraStar className="bottom-[42px] left-[70px] h-[29px] w-5 rotate-[14.1deg]" />
              <SorraCircle className="right-[594px] top-[33px] h-3 w-3" />
              <SorraStar className="right-[206px] top-[18px] h-[26px] w-[18px] rotate-[15.7deg]" />
              <SorraCircle className="right-[18px] top-[35px] h-3 w-3" />
              <SorraStar className="right-[47px] top-[175px] h-[29px] w-[20px]" />
              <SorraCircle className="right-[37px] top-[385px] h-2.5 w-2.5" />
              <SorraStar className="bottom-[16px] right-[192px] h-[22px] w-[15px]" />
              <SorraCircle className="bottom-[16px] left-[472px] h-2.5 w-2.5" />
            </div>
            <p className="absolute left-[18px] top-3.5 text-[10px] text-sorra-pink">
              推廣生意的競賽牌照號碼：60956-7 (只適用於抽獎)
            </p>
            <div
              ref={desktopFrameRef}
              style={{
                boxShadow: '-8px 8px 0 0 #F8A5B1',
              }}
              className="absolute left-[71px] right-[71px] top-[47px] aspect-[1282/529] rounded-full border-[2px] border-[#4F4E4F] bg-[#FFFCFB]"
            >
              <div className="absolute left-[175px] right-[372px] top-[45px] flex flex-col">
                <div className="flex items-end gap-x-1.5">
                  <img
                    src="/images/beauty-award/2025/sasa.png"
                    className="h-[44px] w-[124px] object-contain"
                  />
                  <p className="text-lg font-normal text-[#414042]">
                    美麗「程」獻
                  </p>
                </div>
                <img
                  src="/images/beauty-award/2025/2025_sorra_beauty_award.png"
                  className="mt-6 h-[146px] w-[727px]"
                />
                {/* <img
                  src="/images/beauty-award/2025/desktop_fly_me_to_the.png"
                  className="mt-6 h-[48px] w-[732px] object-contain"
                /> */}
                <div className="mt-6 flex w-full flex-row items-end gap-x-2 text-black">
                  <div className="relative">
                    <div className="absolute -left-0.5 -right-0.5 bottom-[10%] h-[50%] bg-[#FFE5E9]"></div>
                    <p className="relative font-rubik text-[32px] font-normal text-[#4F4E4F]">
                      Fly me to the
                    </p>
                  </div>
                  <div className="h-[2px] w-[19px]">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="19"
                      height="2"
                      viewBox="0 0 19 2"
                      fill="none"
                    >
                      <path
                        d="M0.769997 1.58334C1.19526 1.58334 1.54 1.2289 1.54 0.791671C1.54 0.354443 1.19526 0 0.769997 0C0.344737 0 0 0.354443 0 0.791671C0 1.2289 0.344737 1.58334 0.769997 1.58334Z"
                        fill="#4F4E4F"
                      />
                      <path
                        d="M9.65988 1.58334C10.0851 1.58334 10.4299 1.2289 10.4299 0.791671C10.4299 0.354443 10.0851 0 9.65988 0C9.23462 0 8.88989 0.354443 8.88989 0.791671C8.88989 1.2289 9.23462 1.58334 9.65988 1.58334Z"
                        fill="#4F4E4F"
                      />
                      <path
                        d="M17.7099 1.58334C18.1352 1.58334 18.4799 1.2289 18.4799 0.791671C18.4799 0.354443 18.1352 0 17.7099 0C17.2847 0 16.9399 0.354443 16.9399 0.791671C16.9399 1.2289 17.2847 1.58334 17.7099 1.58334Z"
                        fill="#4F4E4F"
                      />
                    </svg>
                  </div>
                  <div className="h-px grow bg-[#4F4E4F]"></div>
                </div>
                <div className="mt-9 flex flex-row gap-x-[90px] text-[#4F4E4F]">
                  {expired ? (
                    <button
                      onClick={() => {
                        router.push({
                          pathname: `/beautyawards2025/result`,
                        });
                      }}
                      className="rounded-full bg-sorra-pink px-10 py-3 text-center font-sans text-base font-medium text-white hover:opacity-70 disabled:pointer-events-none disabled:bg-[#F7AFB8]"
                    >
                      查看票選結果
                    </button>
                  ) : (
                    <>
                      <div className="flex flex-col items-start">
                        <div className="relative">
                          <div className="absolute -left-0.5 -right-0.5 bottom-[10%] h-[40%] bg-[#FFE5E9]"></div>
                          <p className="relative font-rubik text-lg font-normal text-[#4F4E4F]">
                            Flight
                          </p>
                        </div>
                        <p className="text-[24px] leading-[150%]">SR 2025</p>
                      </div>
                      <div className="flex flex-col items-start">
                        <div className="relative">
                          <div className="absolute -left-0.5 -right-0.5 bottom-[10%] h-[40%] bg-[#FFE5E9]"></div>
                          <p className="relative font-rubik text-lg font-normal text-[#4F4E4F]">
                            Voting Period
                          </p>
                        </div>
                        <p className="text-[24px] leading-[150%]">
                          22/12 - 11/01
                        </p>
                      </div>
                    </>
                  )}
                </div>
                {/* <img
                  src="/images/beauty-award/2025/desktop_flight_period.png"
                  className="mt-9 h-[63px] w-[326px] object-contain"
                /> */}
              </div>
              <div
                className={classnames(
                  'absolute bottom-[67px] left-[974px] right-[89px] flex h-[317px] flex-col items-center overflow-visible rounded-3xl border py-[46px]',
                  expired ? 'border-transparent' : 'border-[#4F4E4F]'
                )}
              >
                {!expired && (
                  <>
                    <img
                      src="/images/beauty-award/2025/desktop_vote.png"
                      className="absolute left-0 top-0 h-[34px] w-[92px] -translate-x-[60%] -translate-y-full rotate-[4.33deg]"
                    />
                    <div className="h-[125px] w-[125px]">
                      <img
                        src="/images/beauty-award/2025/qrcode.png"
                        className="h-full w-full object-contain"
                      />
                    </div>
                    <p className="mt-7 text-center text-lg font-bold text-sorra-pink">
                      使用手機掃描
                      <br />
                      QR Code開始投票
                    </p>
                  </>
                )}
                <img
                  src="/images/beauty-award/2025/plate.png"
                  className="absolute bottom-5 right-0 h-[135px] w-[103px] translate-x-1/2 object-contain"
                />

                <div className="absolute bottom-0 right-0 translate-x-[10%] translate-y-1/2 rotate-[-4.496deg] border border-[#474748] bg-[#FFFDFE] px-2.5 py-1.5">
                  <p className="whitespace-nowrap text-[26px] font-bold tracking-[1.3px] text-[#4A4A4A]">
                    選出陪你渡過2025旅程的
                    <span className="text-sorra-pink">年度美妝好物</span>!
                  </p>
                </div>
              </div>
            </div>
            <img
              ref={ballonRef}
              src="/images/beauty-award/2025/ballon.png"
              className="absolute left-[100px] top-[50px] h-[135px] w-[135px] rotate-[40deg] object-contain"
            />
            <img
              ref={redBowRef}
              src="/images/beauty-award/2025/pink_bow.png"
              className="absolute bottom-[240px] left-10 h-[85px] w-[80px] rotate-[5.61deg] object-contain"
            />
            <img
              ref={blueBowRef}
              src="/images/beauty-award/2025/blue_bow.png"
              className="absolute right-[16px] top-[220px] h-[135px] w-[135px] -rotate-[8.5deg] object-contain"
            />
            <img
              ref={lipRef}
              src="/images/beauty-award/2025/lip.png"
              className="absolute bottom-5 left-[132px] h-[171px] w-[65px] -rotate-[3.04deg] object-contain"
            />
            <img
              ref={planeRef}
              src="/images/beauty-award/2025/plane.png"
              className="absolute right-[54px] top-[12px] h-[154px] w-[231px] -rotate-[0.88deg] object-contain"
            />
          </div>
          <div className="h-[98px]" />
          {/* <ScrollingGifts /> */}
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <ScrollingGifts awards={awards} />
        </div>
      </div>
      <div className="relative block overflow-x-hidden bg-[#FFCCD4] text-white lg:hidden">
        {/* <SorraCircle className="absolute right-14 top-7 h-3 w-3" /> */}
        {/* <SorraStar className="absolute right-7 top-9 h-[29px] w-5" /> */}
        <div className="bg-white">
          <div
            className="relative flex min-h-dvh flex-col"
            style={{
              background:
                'linear-gradient(206deg, #FFE5E9 35.86%, rgba(235, 89, 148, 0.50) 73.84%)',
            }}
          >
            <div className="flex items-end justify-center gap-x-1.5 pb-11 pt-7">
              <img
                src="/images/beauty-award/2025/sasa.png"
                className="h-[39px] w-[109px] object-contain"
              />
              <br />
              <p className="text-sm font-normal text-[#414042]">美麗「程」獻</p>
            </div>
            <div className="relative flex grow flex-col px-4.5">
              <div className="grow rounded-t-[120px] border-[2px] border-[#4F4E4F] bg-[#FFFCFB] px-8 pb-3 pt-10">
                <img
                  src="/images/beauty-award/2025/2025.png"
                  className="absolute left-1/2 top-0 h-[41px] w-[115px] -translate-x-1/2 -translate-y-1/2"
                />
                <div className="flex justify-center">
                  <img
                    className="h-[95px] w-[238px]"
                    src="/images/beauty-award/2025/mobile_sorra_beauty.png"
                  />
                </div>
                {isLoginPage && (
                  <>
                    <p className="mt-7 text-center font-jinxuanlatte text-[24px] font-bold text-[#4F4E4F]">
                      謝謝投選，立即抽獎
                      <br />
                      立即登入/註冊
                      <br />
                      即抽即中！
                    </p>
                    <div className="mb-5 mt-5 flex flex-col gap-y-4 px-4 text-sm">
                      <button
                        className="flex items-center justify-center gap-x-2 rounded-lg bg-[#1877F2] p-2"
                        onClick={() => {
                          localStorage.setItem(
                            'beauty_award_signup_action',
                            'facebook'
                          );
                          redirectToLogin(asPath);
                        }}
                      >
                        <img
                          className="h-6 w-6 object-contain"
                          src="/images/2024/fb.png"
                        />
                        <p>使用Facebook登入</p>
                      </button>
                      <button
                        className="flex items-center justify-center gap-x-2 rounded-lg border border-[#9B9B9B] bg-white p-2 text-[#0000008A]"
                        onClick={() => {
                          localStorage.setItem(
                            'beauty_award_signup_action',
                            'google'
                          );
                          redirectToLogin(asPath);
                        }}
                      >
                        <img
                          className="h-6 w-6 object-contain"
                          src="/images/2024/google.png"
                        />
                        <p>使用Google登入</p>
                      </button>
                      <button
                        className="flex items-center justify-center gap-x-2 rounded-lg border border-sorra-pink p-2 text-sorra-pink"
                        onClick={() => {
                          localStorage.setItem(
                            'beauty_award_signup_action',
                            'email'
                          );
                          redirectToLogin(asPath);
                        }}
                      >
                        <img
                          className="h-6 w-6 object-contain"
                          src="/images/2024/mail.png"
                        />
                        <p>電郵登入/註冊</p>
                      </button>
                    </div>
                    <p className="text-center text-[10px] text-[#4F4E4F]">
                      推廣生意的競賽牌照號碼：60956-7 (只適用於抽獎)
                    </p>
                  </>
                )}

                {!isLoginPage && (
                  <>
                    <div className="mt-8 flex w-full flex-row items-end gap-x-2 text-black">
                      <div className="relative text-base text-black">
                        <div className="absolute -left-0.5 -right-0.5 bottom-[10%] h-[50%] bg-[#FFE5E9]"></div>
                        <p className="relative font-rubik">Fly me to the</p>
                      </div>
                      <div className="mb-1 h-[2px] w-[19px]">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="19"
                          height="2"
                          viewBox="0 0 19 2"
                          fill="none"
                        >
                          <path
                            d="M0.769997 1.58334C1.19526 1.58334 1.54 1.2289 1.54 0.791671C1.54 0.354443 1.19526 0 0.769997 0C0.344737 0 0 0.354443 0 0.791671C0 1.2289 0.344737 1.58334 0.769997 1.58334Z"
                            fill="#4F4E4F"
                          />
                          <path
                            d="M9.65988 1.58334C10.0851 1.58334 10.4299 1.2289 10.4299 0.791671C10.4299 0.354443 10.0851 0 9.65988 0C9.23462 0 8.88989 0.354443 8.88989 0.791671C8.88989 1.2289 9.23462 1.58334 9.65988 1.58334Z"
                            fill="#4F4E4F"
                          />
                          <path
                            d="M17.7099 1.58334C18.1352 1.58334 18.4799 1.2289 18.4799 0.791671C18.4799 0.354443 18.1352 0 17.7099 0C17.2847 0 16.9399 0.354443 16.9399 0.791671C16.9399 1.2289 17.2847 1.58334 17.7099 1.58334Z"
                            fill="#4F4E4F"
                          />
                        </svg>
                      </div>
                      <div className="mb-[5px] h-px grow bg-[#4F4E4F]"> </div>
                    </div>
                    <div className="mt-3 flex w-full flex-row items-end gap-x-11 text-[#4F4E4F]">
                      <div className="flex flex-col items-start">
                        <div className="relative text-xs">
                          <div className="absolute -left-0.5 -right-0.5 bottom-[10%] h-[50%] bg-[#FFE5E9]"></div>
                          <p className="relative">Flight</p>
                        </div>
                        <p className="text-sm font-light">SR 2025</p>
                      </div>
                      <div className="flex flex-col items-start">
                        <div className="relative text-xs">
                          <div className="absolute -left-0.5 -right-0.5 bottom-[10%] h-[50%] bg-[#FFE5E9]"></div>
                          <p className="relative">Voting Period</p>
                        </div>
                        <p className="text-sm font-light">22/12 - 11/01</p>
                      </div>
                    </div>
                    <div className="mt-6 translate-x-[5%] rotate-[-2.8deg] text-[20px] font-medium tracking-[0.57px] text-[#414042]">
                      <div
                        style={{
                          background:
                            'linear-gradient(90deg, #FFF 0%, #FDF7F3 99.98%)',
                        }}
                        className="-translate-x-[35%] border border-[#414042] px-4 py-1"
                      >
                        <p className="text-right">選出陪你渡過2025旅程</p>
                      </div>
                      <div
                        style={{
                          background:
                            'linear-gradient(90deg, #FFF 0%, #FDF7F3 99.98%)',
                        }}
                        className="-translate-y-[1px] translate-x-[45%] border border-[#414042] px-4 py-1"
                      >
                        <p className="text-left">
                          的
                          <span className="text-sorra-pink">年度美妝好物</span>!
                        </p>
                      </div>
                    </div>
                    <div className="mt-6 w-full">
                      <BeautyAwardCTA
                        expired={expired}
                        disableVoting={disableVoting}
                      />
                    </div>
                    <div className="mt-6 flex flex-col items-center">
                      <img
                        src="/images/beauty-award/2025/label.png"
                        className="aspect-[861/124] h-[18px] object-contain"
                      />
                    </div>
                  </>
                )}
              </div>

              <img
                ref={mobilePlaneRef}
                src="/images/beauty-award/2025/plane.png"
                className="absolute left-4 top-0 h-[79px] w-[119px] rotate-[-3.019deg] object-contain"
              />
            </div>
            <div className="absolute bottom-0 left-0 right-0">
              <ScrollingGifts mobile awards={awards} />
            </div>
          </div>
        </div>
        {!isLoginPage && (
          <div className="bg-[#FFE5E9] pt-9">
            <div className="mobileRabbitTrigger relative">
              <SorraCircle className="absolute -top-1 left-1 h-3 w-3" />
              <SorraStar className="absolute left-5 top-0 h-[29px] w-5 -rotate-[9.2deg]" />
              <SorraStar className="absolute left-12 top-[70px] h-[29px] w-5" />
              <div
                style={{ filter: 'blur(50px)' }}
                className="absolute -bottom-5 -left-5 -right-5 -top-5 bg-white"
              ></div>
              <img
                ref={mobileRabbitRef}
                src="/images/beauty-award/2025/rabbit.png"
                className="absolute bottom-0 right-0 h-[119px] w-[98px] -rotate-[9.45deg] object-contain"
              />
              <div className="animateTrigger relative">
                <div className="flex flex-col items-center">
                  <img
                    src="/images/beauty-award/2025/label.png"
                    className="aspect-[861/124] h-[24px] object-contain"
                  />
                </div>
                <div className="flex justify-center">
                  <div className="relative mt-0.5 h-12">
                    <img
                      src="/images/beauty-award/2025/title.png"
                      className="aspect-[420/72] h-full object-contain"
                    />
                    <img
                      src="/images/beauty-award/2025/mobile_qqline.png"
                      className="absolute -bottom-2 right-0 h-[16x] w-[126px]"
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <div className="flex flex-col justify-center">
                    <div className="px-4">
                      <p className="text-center text-sm text-black">
                        Sorra年度票選大賞正式開始！
                        <br />
                        第2屆的主題為「Fly me to the ...」，
                        <br />
                        希望大家在選出陪你渡過2025年旅程的年度好物。
                      </p>
                    </div>
                    <div className="font-jinxuanlatte text-sm font-bold text-[#4F4E4F]">
                      <div className="mt-4.5 flex items-center justify-center">
                        <p>由</p>
                        <div className="relative mx-1.5 flex text-sorra-pink">
                          <div className="absolute left-0 right-0 top-1/2 h-1/3 w-full -translate-y-1/2 bg-[#FFE5E9]" />
                          <SpinningNumber
                            number={35000}
                            animate={animateTrigger}
                          />
                        </div>
                        <p>名認證會員從</p>
                      </div>
                      <div className="flex items-center justify-center">
                        <p>從</p>
                        <div className="relative mx-1.5 flex text-sorra-pink">
                          <div className="absolute left-0 right-0 top-1/2 h-1/3 w-full -translate-y-1/2 bg-[#FFE5E9]" />
                          <SpinningNumber
                            number={4000}
                            animate={animateTrigger}
                          />
                        </div>
                        <p>件商品</p>
                      </div>
                      <div className="flex items-center justify-center">
                        <p>統計</p>
                        <div className="relative mx-1.5 flex text-sorra-pink">
                          <div className="absolute left-0 right-0 top-1/2 h-1/3 w-full -translate-y-1/2 bg-[#FFE5E9]" />
                          <SpinningNumber
                            number={58000}
                            animate={animateTrigger}
                          />
                        </div>
                        <p>篇真實用家選出</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-12 flex justify-center">
              <div className="aspect-[321/124] w-[321px]">
                <img
                  className="h-full w-full"
                  src="/images/beauty-award/2025/sasaxsorra.png"
                />
              </div>
            </div>

            <div className="relative mt-8 flex flex-row items-stretch gap-x-3 px-4 pb-12 font-jinxuanlatte text-sm font-normal text-[#4F4E4F]">
              <div className="relative flex flex-1 flex-col gap-y-2 bg-white p-2 shadow-md">
                <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rotate-[-4.8deg] whitespace-nowrap bg-sorra-pink px-2 py-1 font-bold text-[#FFFDFE]">
                  真實用家評價
                </div>
                <div className="aspect-square bg-[#FFE5E9]">
                  <img
                    src="/images/beauty-award/2025/metric0.png"
                    className="h-full w-full object-contain"
                  />
                </div>
                <p>
                  大賞結果將由2025年間於<span className="font-bold">Sorra</span>
                  所獲的好評數及投票總計得出！反映出廣泛消費者的真實意見~​
                </p>
              </div>
              <div className="relative flex flex-1 flex-col gap-y-2 bg-white p-2 shadow-md">
                <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rotate-[-4.8deg] whitespace-nowrap bg-sorra-pink px-2 py-1 font-bold text-[#FFFDFE]">
                  年度銷售數據
                </div>
                <div className="aspect-square bg-[#FFE5E9]">
                  <img
                    src="/images/beauty-award/2025/metric1.png"
                    className="h-full w-full object-contain"
                  />
                </div>
                <p>
                  <span className="font-bold">
                    ​香港最大型連鎖美妝店
                    <span className="text-sorra-pink">SaSa</span>
                  </span>
                  亦整理其2025年銷售數據，反映消費者真正支持的好用產品！
                </p>
              </div>
            </div>
          </div>
        )}

        {!isLoginPage && (
          <div className="border-y-[2px] border-[#4F4E4F] bg-[#FFF5F4]">
            <div className="mt-12 flex justify-center font-jinxuanlatte text-[36px] font-bold text-sorra-pink">
              <p>立即投選 人人有賞</p>
            </div>
            <div className="mt-2 flex flex-col items-center font-rubik text-base font-bold text-[#4F4E4F]">
              <p className="text-center">
                投選自己最喜歡的12個產品，
                <br />
                完成問卷並註冊，即可參加抽獎！
              </p>
            </div>

            <div className="mb-8 mt-8 flex flex-col gap-y-4">
              {loopItems.map((groups, groupIdx) => {
                return (
                  <div
                    key={groupIdx}
                    className={classnames('flex items-center overflow-hidden', {
                      'justify-end': groupIdx % 2 === 0,
                      'justify-start': groupIdx % 2 === 1,
                    })}
                    onMouseOver={() => {
                      // console.log('hover');
                      setHoverState((prev) => {
                        if (!prev.includes(groupIdx)) {
                          prev.push(groupIdx);
                        }
                        return prev;
                      });
                    }}
                    onMouseOut={() => {
                      setHoverState((prev) => {
                        return _.filter(prev, (idx) => idx !== groupIdx);
                      });
                    }}
                  >
                    {groups.map((_, idx) => {
                      return (
                        <div key={idx} className="flex shrink-0 items-center">
                          {groups.map((group, idx) => {
                            return (
                              <div
                                key={idx}
                                className={classnames(
                                  'move-item flex items-center',
                                  {
                                    'animate-full-right': groupIdx % 2 === 0,
                                    'animate-full-left': groupIdx % 2 === 1,
                                    '!pause': hoverState.includes(groupIdx),
                                  }
                                )}
                              >
                                {group.map((item, idx) => {
                                  return (
                                    <div
                                      key={idx}
                                      className="ml-2 rounded-full border border-[#4F4E4F] bg-white px-7 py-1.5 font-jinxuanlatte text-lg font-bold text-[#F16B7C]"
                                    >
                                      {item}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            <div className="mb-12 w-full px-[54px]">
              <BeautyAwardCTA expired={expired} disableVoting={disableVoting} />
            </div>
          </div>
        )}

        <div className="flex flex-col justify-center gap-y-3 px-7 pb-8 pt-10">
          <div className="bg-white">
            <p className="text-center font-jinxuanlatte text-[36px] font-bold text-sorra-pink">
              {isLoginPage ? '20萬獎品等你來拿' : '豐富獎品 即抽即中'}
            </p>
          </div>
          <p className="text-center font-rubik text-base font-bold text-white">
            投選後即抽即中獎，更有機會贏取雙人日本來回機票！
          </p>
        </div>
        <div className="relative overflow-visible">
          <img
            ref={mobileEarthRef}
            src="/images/beauty-award/2025/earth.png"
            className="absolute left-1/2 top-2/3 w-full -translate-x-1/2 scale-[182%]"
          />
          <div className="relative w-full pb-12 pt-2">
            <div className="relative h-[286px] w-full">
              <RotateItem awards={awards} awardIdx={0} className="feature-0" />
              <RotateItem awards={awards} awardIdx={1} className="feature-1" />
              <RotateItem awards={awards} awardIdx={2} className="feature-2" />
              <RotateItem awards={awards} awardIdx={0} className="feature-3" />
              <RotateItem awards={awards} awardIdx={1} className="feature-4" />
              <RotateItem awards={awards} awardIdx={2} className="feature-5" />
            </div>
          </div>
        </div>

        {/* <div className="pb-10 pt-4"></div> */}
        <div className="relative mt-8 overflow-visible">
          <div
            style={{
              boxShadow: '4px 0 10px 0 rgba(0, 0, 0, 0.05)',
            }}
            className="relative mx-4 rounded-[24px] border border-[#4F4E4F] bg-white px-6 pb-8 pt-8"
          >
            <div className="prize tracking-[9px] opacity-20">
              <p className="text-center font-jinxuanlatte text-[60px] font-bold text-sorra-pink">
                其他獎品
              </p>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3">
              {_.takeRight(awards, awards.length - 3).map((award, idx) => {
                const { name, display_name: displayName, price, image } = award;
                return (
                  <div
                    key={idx}
                    className="award relative flex flex-col items-center"
                  >
                    <div className="h-[100px] w-[103px] shrink-0">
                      <WebpImg
                        src={
                          image ? image.url : '/images/placeholders/product.png'
                        }
                        className="h-full w-full object-contain"
                      />
                      {/* <img
                        src={`/images/beauty-award/2025/gifts/${idx + 3}.png`}
                        className="h-full w-full object-contain"
                      /> */}
                    </div>
                    <p className="mt-2 whitespace-pre-line text-center font-rubik text-base text-black">
                      {displayName || name}
                    </p>

                    <p className="text-xs text-black">{price}</p>
                    {/* <div className="mt-2 rounded-full bg-[#F16B7C] px-2 py-1 text-xs font-medium text-white">
                      {_.isNumber(count) ? `名額${count}位` : count}
                    </div> */}
                  </div>
                );
              })}
            </div>
            {!isLoginPage && (
              <div className="mt-8 w-full">
                <BeautyAwardCTA
                  expired={expired}
                  disableVoting={disableVoting}
                />
              </div>
            )}
          </div>
        </div>

        <div className="px-8 pb-8 pt-[26px] text-[10px]">
          <div>
            <p className="py-2">
              推廣生意的競賽牌照號碼：60956-7 (只適用於抽獎)
            </p>
            <p className="pb-2">
              「SaSa X SORRA 2025年度票選大賞」推廣條款及細則
            </p>
            <ol className="list-decimal pl-4">
              <li>
                「SaSa X SORRA 2025年度票選大賞」推廣期為 2025年 12 月 22 日至
                2026 年 1 月 11 日（含首尾兩日）（下稱「推廣期」）。
              </li>
              <li>
                參加本推廣代表會員接受本條款及細則（「條款及細則」）。會員參與推廣活動時，需持有經認證的
                sorra.net 註冊帳戶 （下稱「會員」）。
              </li>
              <li>
                會員於推廣期內參加票選，如成功提交票選表格後將獲得1次「抽獎」機會，每位會員只會獲得1次抽獎機會。
              </li>
              <li>
                於「SaSa X SORRA
                2025年度票選大賞」機會中，會員有可能獲得以下其中一項獎品：由商戶提供的產品／商戶優惠碼／服務兌換券；sorra.net
                平台積分；香港至東京來回機票（詳見「機票獎品之特別條款及細則」）；或其他指定獎品。
              </li>
              <li>
                抽獎結果一旦確定後將不能更改。「SaSa X SORRA
                2025年度票選大賞」獎品的中獎率並非平均分佈。
              </li>
              <li>
                獎品將會以平郵、順豐速遞寄出或於 SaSa
                指定門市領取。如經順豐速遞寄出，Sorra將會於sorra.net平台上收取相關郵寄費用。
              </li>
              <li>
                中獎後，sorra.net平台將要求會員填寫郵寄地址。若會員於2026年2月28日前仍未提交相關資料，Sorra
                將默認會員放棄有關獎品。
              </li>
              <li>
                如獲得sorra.net平台積分，相關積分將於7個工作天内存入會員賬戶。
              </li>
              <li>
                在第 4 條中提到的商戶優惠碼／服務兌換券、Sorra.net
                平台積分及其他指定獎品均有個別的使用條款及細則，參加者在使用前需要留意所持有優惠券的條款及細則內容。商戶優惠券／商戶優惠碼／服務兌換券均由商戶提供，使用細則包括服務、貨品、價錢、享用時間和使用方式等等均由有關商戶提供。詳情請查看相關使用條款或向有關商戶查詢，如果參加者未能成功兌換特定的產品／優惠／服務，Sorra
                一概不會負責。
              </li>
              <li>
                在「SaSa X SORRA
                2025年度票選大賞」推廣過程中，如果參加者有任何非法、欺詐或異常行為，Sorra
                應取消該參加者所獲得的獎品及／或獲得獎品的權利，並保留採取任何必要法律行動的權利。
              </li>
              <li>
                活動完結後，活動完結聲明將於2026年1月20日於英文虎報及經濟日報刊登。
              </li>
            </ol>
            <p className="py-2 font-bold">機票獎品之特別條款及細則</p>
            <p className="pb-2">
              若會員抽中「香港至東京來回機票」（下稱「機票」），須受以下特別條款約束：
            </p>
            <ol className="list-decimal pl-4" start={12}>
              <li>
                Sorra
                Limited將於活動結束後10～20個工作天內，以Whatsapp或電話聯絡得獎者有關機票的換領詳情。如得獎者未能收到Sorra聯絡，請務必於2026年3月1日前電郵至
                info@sorra.net 與Sorra客戶服務部聯絡，逾期作廢。
              </li>
              <li>
                機票的有效旅行期由聯絡當日起的一個月後至2026年12月31日止（即回程須不遲於2026年12月31日完成）。機票只適用於香港至東京的來回旅程。不適用日期包括：2026年2月13日至2月25日、2026年4月1日至4月10日、2026年4月28日至5月7日、2026年7月15日至8月25日、2026年12月18日至12月31日及航空公司指定的其他不適用日子。訂位確認需視乎航班機位供應情況而定，由
                Sorra 及航空公司全權決定。{' '}
              </li>
              <li>
                機票之旅行有效期（逗留期限）為不多於 30
                天。機票一經確認及出票，不得修改、取消、退款、更換或重新訂位。
              </li>
              <li>
                機票獎品僅包含淨票價。乘客須負責所有適用之額外費用或收費，包括但不限於機場稅、燃油附加費、機場建設費及與機票有關的行政服務費等。Sorra將於預訂時向得獎者收取該等費用。
              </li>
              <li>
                機票不可轉讓或售賣，且機票僅可由得獎者本人及得獎者指定的一位賓客兌換。兩套機票必須為同一出發及回程航班（即二人同行，同進同出）。
              </li>
              <li>
                合資格得獎者必須於換領機票時向Sorra提供其本人及賓客之有效護照全名（必須與護照相符）、聯絡電話及電郵以作預訂。
              </li>
              <li>兌換及預訂機票時必須出示得獎者之香港身份證正本以作核實。</li>
              <li>
                所有機票均視乎訂位時的機位供應而定。Sorra
                不保證特定航班或日期的供應情況。
              </li>
              <li>
                Sorra
                並非航空服務供應商，對航空公司提供的相關服務（包括但不限於航班延誤、取消、行李遺失或意外）之任何事宜概不負責。
              </li>
              <li>
                如對機票有任何爭議，Sorra 的決定為最終決定，並具有約束力。
              </li>
            </ol>
            <p className="py-2 font-bold">其他一般條款</p>
            <ol className="list-decimal pl-4" start={22}>
              <li>
                所有「SaSa X SORRA
                2025年度票選抽獎活動」獎品或積分均不可以退換、轉讓或兌換成現金。
              </li>
              <li>
                若因本推廣條款及細則或任何溝通過程中產生的任何歧義、疑問或爭議，Sorra
                及莎莎化粧品有限公司 擁有最終決策權而無需告知理由。
              </li>
              <li>如參加者對本推廣有任何疑問，可電郵至info@sorra.net查詢。</li>
              <li>
                本條款及細則受香港特別行政區法律管轄，並按其進行解釋。當事人受到香港特別行政區法院管轄。
              </li>
              <li>
                如有任何爭議，Sorra 及莎莎化粧品有限公司將保留最終決定權。
              </li>
            </ol>
            <p className="py-2">
              推廣生意的競賽牌照號碼：60956-7 (只適用於抽獎)
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default BeautyAwards2025;

export async function getServerSideProps(ctx) {
  const { key } = ctx.query;
  try {
    const { data: campaigns } = await api.request({
      method: 'GET',
      url: `/api/v3/campaigns`,
    });
    const targetCampaign = _.find(campaigns, { name: 'BEAUTY_AWARD_2025' });
    const { data: beautyAward } = await api.request({
      method: 'GET',
      url: `/api/beauty-award/2025/index`,
    });

    if (targetCampaign || key === 'F5A3302A-E6AB-44E7-ABEB-D41DECD1DCAA') {
      return {
        props: {
          expired: false,
          awards: beautyAward.awards,
        },
      };
    } else {
      return {
        props: {
          expired: true,
          awards: beautyAward.awards,
        },
      };
    }
  } catch (e) {
    console.log(e);
  }
  return {
    notFound: true,
  };
}
